import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Link,
  Alert,
} from '@mui/material';

interface Verify2FADialogProps {
  open: boolean;
  onClose: () => void;
  onVerify: (code: string) => Promise<void>;
  onUseRecoveryCode?: () => void;
  error?: string | null;
}

export const Verify2FADialog: React.FC<Verify2FADialogProps> = ({
  open,
  onClose,
  onVerify,
  onUseRecoveryCode,
  error,
}) => {
  const [code, setCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleVerify = async () => {
    setIsSubmitting(true);
    try {
      await onVerify(code);
      setCode('');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setCode('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>Two-Factor Authentication</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Typography variant="body2" gutterBottom>
          Enter the 6-digit code from your authenticator app
        </Typography>

        <TextField
          fullWidth
          label="Authentication Code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          inputProps={{ maxLength: 6 }}
          sx={{ mt: 2 }}
          autoFocus
          onKeyPress={(e) => {
            if (e.key === 'Enter' && code.length === 6) {
              handleVerify();
            }
          }}
        />

        {onUseRecoveryCode && (
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Link
              component="button"
              variant="body2"
              onClick={() => {
                handleClose();
                onUseRecoveryCode();
              }}
            >
              Use recovery code instead
            </Link>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          onClick={handleVerify}
          variant="contained"
          disabled={code.length !== 6 || isSubmitting}
        >
          {isSubmitting ? 'Verifying...' : 'Verify'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
